import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Eye, Loader2, RefreshCw } from 'lucide-react';
import { aiApi } from '../api';
import MarkdownRenderer from './MarkdownRenderer';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';

dayjs.locale('ru');

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

interface PageContext {
  pageType: string;
  entityType?: string;
  entityId?: string;
  pagePath: string;
}

function resolvePageContext(pathname: string): PageContext {
  const segments = pathname.split('/').filter(Boolean);
  const pagePath = pathname;

  if (pathname === '/') return { pageType: 'dashboard', pagePath };
  if (pathname === '/calendar') return { pageType: 'calendar', pagePath };
  if (pathname === '/api-keys') return { pageType: 'api_keys', pagePath };
  if (pathname === '/api-docs') return { pageType: 'api_docs', pagePath };
  if (pathname === '/admin') return { pageType: 'admin', pagePath };
  if (pathname === '/profile') return { pageType: 'profile', pagePath };

  if (segments.length === 1) {
    const listMap: Record<string, string> = {
      deals: 'deals_list',
      companies: 'companies_list',
      contacts: 'contacts_list',
      leads: 'leads_list',
      tasks: 'tasks_list',
    };
    const pt = listMap[segments[0]];
    if (pt) return { pageType: pt, pagePath };
  }

  if (segments[0] === 'deals' && segments.length === 1) {
    return { pageType: 'deals_kanban', pagePath };
  }

  if (segments.length === 2) {
    const entityMap: Record<string, { pageType: string; entityType: string }> = {
      companies: { pageType: 'company_detail', entityType: 'company' },
      contacts: { pageType: 'contact_detail', entityType: 'contact' },
      leads: { pageType: 'lead_detail', entityType: 'lead' },
      deals: { pageType: 'deal_detail', entityType: 'deal' },
      tasks: { pageType: 'task_detail', entityType: 'task' },
    };
    const mapping = entityMap[segments[0]];
    if (mapping) {
      return { pageType: mapping.pageType, entityType: mapping.entityType, entityId: segments[1], pagePath };
    }
  }

  return { pageType: 'dashboard', pagePath };
}

const pageLabels: Record<string, string> = {
  dashboard: 'Дашборд',
  deals_kanban: 'Канбан воронки',
  deals_list: 'Список сделок',
  companies_list: 'Список компаний',
  contacts_list: 'Список контактов',
  leads_list: 'Список лидов',
  tasks_list: 'Список задач',
  company_detail: 'Компания',
  contact_detail: 'Контакт',
  lead_detail: 'Лид',
  deal_detail: 'Сделка',
  task_detail: 'Задача',
  calendar: 'Календарь',
  api_keys: 'API-ключи',
  api_docs: 'Документация API',
  admin: 'Администрирование',
  profile: 'Профиль',
};

export default function AiAssistantPanel({ isOpen, onClose }: Props) {
  const location = useLocation();
  const [reply, setReply] = useState<string | null>(null);
  const [userMessage, setUserMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingLast, setLoadingLast] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const pageCtx = resolvePageContext(location.pathname);

  const fetchLast = useCallback(async () => {
    setLoadingLast(true);
    setError(null);
    try {
      const res = await aiApi.getLastResponse({
        pageType: pageCtx.pageType,
        entityType: pageCtx.entityType,
        entityId: pageCtx.entityId,
      });
      if (res.data.reply) {
        setReply(res.data.reply);
        setLastSavedAt(res.data.createdAt);
      } else {
        setReply(null);
        setLastSavedAt(null);
      }
    } catch {
      setReply(null);
    } finally {
      setLoadingLast(false);
    }
  }, [pageCtx.pageType, pageCtx.entityType, pageCtx.entityId]);

  useEffect(() => {
    if (isOpen) {
      fetchLast();
    }
  }, [isOpen, fetchLast]);

  useEffect(() => {
    if (bodyRef.current && reply) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [reply]);

  const handleSend = useCallback(async () => {
    setLoading(true);
    setError(null);
    setReply('');
    try {
      const response = await aiApi.chat({
        pageType: pageCtx.pageType,
        pagePath: pageCtx.pagePath,
        entityType: pageCtx.entityType,
        entityId: pageCtx.entityId,
        userMessage: userMessage.trim() || undefined,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        setError(err?.error || 'Ошибка при обращении к ассистенту');
        setLoading(false);
        return;
      }

      setUserMessage('');
      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed || !trimmed.startsWith('data: ')) continue;
          const payload = trimmed.slice(6);

          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) {
              setError(parsed.error);
            } else if (parsed.done) {
              if (parsed.createdAt) setLastSavedAt(parsed.createdAt);
            } else if (parsed.chunk) {
              accumulated += parsed.chunk;
              setReply(accumulated);
            }
          } catch {
            // skip malformed events
          }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Ошибка при обращении к ассистенту');
    } finally {
      setLoading(false);
    }
  }, [pageCtx, userMessage]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!loading && (userMessage.trim() || reply)) {
        handleSend();
      }
    }
  };

  return (
    <>
      {isOpen && <div className="ai-overlay" onClick={onClose} />}
      <div className={`ai-panel ${isOpen ? 'ai-panel-open' : ''}`}>
        <div className="ai-panel-header">
          <div className="ai-panel-header-left">
            <Eye size={22} className="ai-eye-icon" />
            <span className="ai-panel-title">ИИ-Ассистент</span>
            <span className="ai-panel-page">{pageLabels[pageCtx.pageType] || pageCtx.pageType}</span>
          </div>
          <div className="ai-panel-header-actions">
            <button className="ai-header-btn" onClick={fetchLast} title="Обновить" disabled={loadingLast}>
              <RefreshCw size={16} className={loadingLast ? 'ai-spin' : ''} />
            </button>
            <button className="ai-header-btn" onClick={onClose} title="Закрыть">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="ai-panel-body" ref={bodyRef}>
          {loadingLast && (
            <div className="ai-loading">
              <Loader2 size={24} className="ai-spin" />
              <span>Загрузка...</span>
            </div>
          )}

          {!loadingLast && !reply && !loading && (
            <div className="ai-empty">
              <Eye size={56} />
              <p>Нажмите «Отправить» для анализа данных этой страницы</p>
            </div>
          )}

          {reply && !loadingLast && (
            <div className="ai-response">
              {lastSavedAt && (
                <div className="ai-response-time">
                  {dayjs(lastSavedAt).format('DD.MM.YYYY HH:mm')}
                </div>
              )}
              <MarkdownRenderer content={reply} />
              {loading && <span className="ai-cursor" />}
            </div>
          )}

          {loading && !reply && (
            <div className="ai-loading">
              <Loader2 size={24} className="ai-spin" />
              <span>Анализирую данные...</span>
            </div>
          )}

          {error && (
            <div className="ai-error">
              <p>{error}</p>
            </div>
          )}
        </div>

        <div className="ai-panel-footer">
          <textarea
            ref={textareaRef}
            className="ai-input"
            placeholder="Задайте вопрос или оставьте пустым для автоматического анализа..."
            value={userMessage}
            onChange={(e) => setUserMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={loading}
          />
          <button
            className="ai-send-btn"
            onClick={handleSend}
            disabled={loading}
            title="Отправить"
          >
            {loading ? <Loader2 size={18} className="ai-spin" /> : <Send size={18} />}
          </button>
        </div>
      </div>
    </>
  );
}
